'use client';

import type { Dispatch, SetStateAction } from 'react';

import classNames from 'classnames';
import { Card } from '$src-components/atoms/Card';

import { changeUrl } from '$util/index';

import { GarageDetailsSection } from '@/components/organisms/GDPGarageInfoCard/components/GarageDetailsSection';
import { GarageFooterSection } from '@/components/organisms/GDPGarageInfoCard/components/GarageFooterSection';
import { Button, DesignType } from '@/components/atoms/Button';
import { Img } from '@/components/atoms/Img';
import { GarageInfoCardHeader } from '@/components/atoms/GarageInfoCardHeader';
import { GarageReviewSection } from '@/components/atoms/GarageReviewSection';
import styles from './index.module.scss';
import type { AppGarage } from 'pages/api/getGaragesByLatLong';

export interface GDPGarage {
  readonly name: AppGarage['name'];
  readonly ratingNumber: AppGarage['ratingNumber'];
  readonly reviewsNumber: AppGarage['reviewsNumber'];
  readonly bookingsNumber: AppGarage['bookingsNumber'];
  readonly yearsInBusinessNumber: AppGarage['yearsInBusinessNumber'];
  readonly isFixterGarage: AppGarage['isFixterGarage'];
  readonly review?: AppGarage['review'];
  readonly addressText: AppGarage['addressText'];
  readonly servicesText: AppGarage['servicesText'];
  readonly openingHoursText: AppGarage['openingHoursText'];
  readonly garageId: AppGarage['garageId'];
  readonly slug: AppGarage['slug'];
}

export interface GDPGarageInfoCardProps {
  readonly className?: string;
  readonly garage: GDPGarage;
  readonly setSelectedGarageId: Dispatch<SetStateAction<string>>;
  readonly selectedGarageId: string;
}

export function GDPGarageInfoCard({
  className,
  garage,
  selectedGarageId,
  setSelectedGarageId,
  ...props
}: GDPGarageInfoCardProps): JSX.Element {
  const {
    name,
    reviewsNumber,
    ratingNumber,
    bookingsNumber,
    yearsInBusinessNumber,
    review,
    garageId,
    slug,
    isFixterGarage,
  } = garage;

  const isSelectedGarage = garageId === selectedGarageId;

  return (
    <Card
      className={classNames(
        GDPGarageInfoCard.displayName,
        styles.component,
        className,
        styles.GarageCard,
        isSelectedGarage && styles.SelectedGarage
      )}
      onMouseOver={() => {
        setSelectedGarageId(garageId);
      }}
      onClick={() => {
        changeUrl({ pathname: `/garage/${slug}` });
      }}
      elevation={2}
      cornerRadius={2}
      {...props}
    >
      {isFixterGarage && (
        <div className={styles['fixter-certified-desktop-container']}>
          <Img
            alt="fixter certified garage partner"
            provider="cloudinary"
            src="/certified_garage_corner_hsbyrq.svg"
            width={206}
            height={44}
          />
        </div>
      )}
      <div className={styles.GarageCardLeftSection}>
        <GarageInfoCardHeader
          className={styles.GarageInfoCardHeader}
          name={name}
          reviewsNumber={reviewsNumber}
          ratingNumber={ratingNumber}
        />
        {isFixterGarage && (
          <div className={styles['fixter-certified-mobile-container']}>
            <Img
              alt="fixter certified garage partner"
              provider="cloudinary"
              src="/Certified_garage_r3cjw9.png"
              width={120}
              height={25}
            />
          </div>
        )}
        <GarageDetailsSection garage={garage} />
        <Button className={styles.GarageCtaButton} designType={DesignType.PRIMARY} href={`/garage/${slug}`}>
          See more
        </Button>
      </div>

      <div className={styles.GarageCardRightSection}>
        {review && (
          <GarageReviewSection
            rating={review.rating}
            text={review.text}
            authorName={review.authorName}
            relativeTime={review.relativeTime}
          />
        )}

        {isFixterGarage && (
          <GarageFooterSection
            bookingsNumber={bookingsNumber}
            yearsInBusinessNumber={yearsInBusinessNumber}
          />
        )}
      </div>
    </Card>
  );
}

GDPGarageInfoCard.displayName = 'GDPGarageInfoCard';
